import { useEffect, useRef } from 'react'
import { ScrollCue } from '../../../../components/site-hero/ScrollCue'
import { useCopy } from '../../content'
import { HERO_VIDEO, heroVideoFiles, pickSource } from '../../media/hero-video'
import { Picture } from '../media/Picture'

type NavigatorWithConnection = Navigator & { connection?: { saveData?: boolean; effectiveType?: string } }
const SLOW_LINKS = new Set(['slow-2g', '2g'])
const PLAY_EVENTS = ['click', 'touchend', 'keydown'] as const

/**
 * Verde Vercelli's hero, delivered like VV2's: the poster frame is the LCP image
 * (preloaded by head.ts, `sizes="100vw"`); the clip has no source in the markup
 * (`data-srcs` for check-prerender), loads after the window `load` event, fades in
 * on `playing`, and is skipped under reduced motion, Save-Data or 2G. A refused
 * autoplay is retried on the next click / touchend / keydown. The pill is a
 * plain anchor to the contact band (`#contatti`), like the «Scorri» cue.
 */
export function Hero() {
  const copy = useCopy()
  const videoRef = useRef<HTMLVideoElement>(null)
  useEffect(() => {
    const v = videoRef.current
    const clip = HERO_VIDEO // a const local keeps the null-narrowing inside the closures below
    if (clip === null || !v) return
    const c = (navigator as NavigatorWithConnection).connection
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches || c?.saveData === true || SLOW_LINKS.has(c?.effectiveType ?? '')) return
    v.muted = true; v.setAttribute('muted', ''); v.setAttribute('playsinline', '')
    let alive = true
    const retry = () => { v.play().then(unlisten, () => {}) }
    const listen = () => PLAY_EVENTS.forEach((e) => document.addEventListener(e, retry, { passive: true }))
    const unlisten = () => PLAY_EVENTS.forEach((e) => document.removeEventListener(e, retry))
    const onPlaying = () => v.setAttribute('data-playing', '')
    const onEnded = () => { v.currentTime = 0; v.play().catch(() => {}) }
    let timer = 0
    const start = () => {
      v.loop = clip.loop; v.preload = 'auto'
      v.src = pickSource(clip, (m) => window.matchMedia(m).matches).src
      timer = window.setTimeout(() => v.play().catch(() => { if (alive) listen() }), 100)
    }
    v.addEventListener('playing', onPlaying); v.addEventListener('ended', onEnded)
    if (document.readyState === 'complete') start(); else window.addEventListener('load', start, { once: true })
    return () => { alive = false; window.removeEventListener('load', start); window.clearTimeout(timer); unlisten(); v.removeEventListener('playing', onPlaying); v.removeEventListener('ended', onEnded) }
  }, [])
  return (
    <section id="hero" className="relative min-h-[100svh] flex flex-col justify-center overflow-hidden text-brand-cream">
      <div className="absolute inset-0">
        <Picture role="hero" alt="" sizes="100vw" priority fill className="absolute inset-0" imgClassName="h-full w-full object-cover" />
        {HERO_VIDEO && (
          <video ref={videoRef} data-srcs={heroVideoFiles(HERO_VIDEO).join(' ')} muted playsInline preload="none" aria-hidden="true" tabIndex={-1} disablePictureInPicture
            className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-[900ms] ease-out data-[playing]:opacity-100 [&::-webkit-media-controls]:!hidden" />
        )}
      </div>
      {/* Layer 2: the old site's veil, verbatim. */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{ background: `linear-gradient(180deg, rgb(10 31 21 / 0.55) 0%, rgb(0 0 0 / 0.35) 35%, rgb(0 0 0 / 0.55) 100%)` }} />
      {/* pb-28: the content clears the «Scorri» cue at the foot (≈ 5.5rem with its bottom-6). */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center pt-[18svh] sm:pt-[20svh] px-5 sm:px-8 pb-28 text-center">
        <h1 tabIndex={-1} className="fade-rise font-display text-brand-cream text-5xl sm:text-7xl lg:text-8xl leading-[1.0]" style={{ animationDelay: '0.45s', letterSpacing: '0.04em', fontWeight: 700 }}>
          <span
            style={{
              textShadow:
                '0 2px 4px rgba(0,0,0,0.85), 0 4px 30px rgba(0,0,0,0.7), 0 8px 60px rgba(0,0,0,0.45)',
            }}
          >Verde</span>
          {' '}
          <span
            style={{
              whiteSpace: 'nowrap',
              textShadow:
                '0 2px 4px rgba(0,0,0,0.85), 0 4px 30px rgba(0,0,0,0.7), 0 8px 60px rgba(0,0,0,0.45)',
            }}
          >Vercell<span style={{ position: 'relative' }}>&#x0131;<span
            style={{
              position: 'absolute',
              top: '0.15em',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '0.2em',
              height: '0.2em',
              borderRadius: '50%',
              background: 'rgb(22,101,52)',
              boxShadow: '0 0 10px rgba(22,101,52,0.6), 0 0 25px rgba(22,101,52,0.25)',
            }}
          /></span></span>
        </h1>
        <p className="fade-rise mt-4 sm:mt-5 text-brand-cream text-base sm:text-xl tracking-[0.18em] uppercase font-bold" style={{ animationDelay: '0.6s', textShadow: '0 1px 2px rgba(0,0,0,0.95), 0 0 12px rgba(0,0,0,0.85), 0 0 30px rgba(0,0,0,0.6)' }}>{copy.hero.wordmarkSub}</p>
        <div className="fade-rise mt-8 sm:mt-10" style={{ animationDelay: '0.75s' }}>
          <a
            href="#contatti"
            data-track="cta_contatti"
            className="group inline-flex items-center gap-2 px-5 sm:px-7 py-2.5 sm:py-3
                       rounded-full
                       bg-white/25 backdrop-blur-2xl border border-white/40
                       text-white
                       text-sm sm:text-base font-bold uppercase tracking-[0.14em]
                       shadow-2xl shadow-black/50
                       hover:bg-white/35 hover:border-white/55
                       transition-all duration-300 ease-out
                       hover:-translate-y-0.5"
          >
            {/* The long label would wrap the pill on phones; the short one keeps «gratuito». */}
            <span className="sm:hidden">{copy.hero.ctaPrimaryShort}</span>
            <span className="hidden sm:inline">{copy.hero.ctaPrimary}</span>
            <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
          </a>
        </div>
      </div>
      <ScrollCue href="#chi-siamo" label={copy.hero.scrollCue} compact />
    </section>
  )
}
